import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getAdminKey, getApiBase, opsFetch } from "../lib/api";
import { Count, Empty } from "../components/ui";

type WaitlistRes = {
  data: {
    items: Array<{
      id: string;
      email: string;
      whatsapp?: string | null;
      trip_month?: string | null;
      city?: string | null;
      source_page?: string | null;
      created_at?: string | null;
    }>;
    total: number;
    page: number;
  };
};

export function WaitlistPage() {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["ops-waitlist", search],
    queryFn: () =>
      opsFetch<WaitlistRes>(
        `/api/ops/waitlist?per_page=100${search ? `&q=${encodeURIComponent(search)}` : ""}`,
      ),
  });

  function exportCsv() {
    const url = `${getApiBase()}/api/ops/waitlist/export.csv`;
    fetch(url, { headers: { "X-ChinaPie-Content-Admin-Key": getAdminKey() } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "chinapie-waitlist.csv";
        a.click();
      });
  }

  return (
    <div className="ops-panel">
      <div className="ops-toolbar">
        <input
          className="ops-input"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSearch(q.trim());
          }}
          placeholder="Search email or city"
          value={q}
        />
        <button className="ops-btn" onClick={() => setSearch(q.trim())} type="button">
          Search
        </button>
        <button className="ops-btn ops-btn--primary" onClick={exportCsv} type="button">
          Export CSV
        </button>
        {query.data ? <Count label="signups" n={query.data.data.total} /> : null}
      </div>
      {query.data && query.data.data.items.length > 0 ? (
        <p className="ops-callout ops-callout--ok">
          Cities {topLabels(query.data.data.items.map((item) => item.city))}. Sources {topLabels(query.data.data.items.map((item) => item.source_page))}.
        </p>
      ) : null}
      {query.isLoading ? <p className="ops-muted">Loading…</p> : null}
      {query.isError ? <p className="ops-error">{(query.error as Error).message}</p> : null}
      {query.data && query.data.data.items.length === 0 ? <Empty>No signups match this search.</Empty> : null}
      {query.data && query.data.data.items.length > 0 ? (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>WhatsApp</th>
                <th>City</th>
                <th>Trip</th>
                <th>Source</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {query.data.data.items.map((row) => (
                <tr key={row.id}>
                  <td>{row.email}</td>
                  <td>{row.whatsapp || "—"}</td>
                  <td>{row.city || "—"}</td>
                  <td>{row.trip_month || "—"}</td>
                  <td className="ops-muted">{row.source_page || "—"}</td>
                  <td className="ops-muted">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function topLabels(values: Array<string | null | undefined>) {
  const counts = new Map<string, number>();
  for (const value of values) {
    const key = (value || "").trim() || "unknown";
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  return top.length ? top.map(([name, n]) => `${name} ${n}`).join(" · ") : "none yet";
}
